import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Briefcase, Building2, User, Mail, Phone, FileCheck, Car, ArrowRight,
  UploadCloud, CheckCircle2, Loader2, ArrowLeft, ShieldCheck, Camera,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import useDocumentMeta from "@/lib/useDocumentMeta";
import { compressImageFile } from "@/lib/imageCompression";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Document slots — kept in sync with backend/routers/driver_applications.py::DOC_SLOTS.
// `vehicleDoc: true` means the slot is skipped when the applicant doesn't
// have their own vehicle (company vehicle assigned after hire).
const DOC_SLOTS = [
  { key: "prometna_dozvola",     label: "Prometna dozvola",                     required: ["A", "B"], vehicleDoc: true },
  { key: "polica_osiguranja",    label: "Polica osiguranja",                    required: ["A", "B"], vehicleDoc: true },
  { key: "taxi_certificate",     label: "Taxi certifikat (svjedodžba o stručnoj osposobljenosti)", required: ["A", "B"], vehicleDoc: false },
  { key: "drivers_licence",      label: "Vozačka dozvola",                       required: ["A", "B"] },
  { key: "id_document",          label: "Osobna iskaznica / putovnica",         required: ["A", "B"] },
  { key: "headshot",             label: "Profilna fotografija",                 required: ["A", "B"] },
  { key: "criminal_record",      label: "Potvrda o nekažnjavanju",              required: ["A", "B"] },
  { key: "firm_extract",         label: "Izvadak iz sudskog / obrtnog registra",required: ["B"] },
];

const EMPTY_FORM = {
  first_name: "", last_name: "", email: "", phone: "", oib: "",
  date_of_birth: "", address: "", city: "", country: "Hrvatska",
  employment_type: "full_time", driving_experience_years: "",
  company_name: "", company_oib: "", company_iban: "",
  iban: "", taxi_license_status: "not_yet",
  vehicle_make: "", vehicle_model: "", vehicle_color: "",
  vehicle_vin: "", vehicle_plates: "", vehicle_year: "", eco_category: "",
  consent_data_processing: false, consent_marketing: false, referral_source: "",
};

export default function BecomeDriver() {
  useDocumentMeta({
    title: "Postani vozač · Juke Transfers",
    description:
      "Prijava za vozača ili B2B partnera Juke Transfers. Dvostruki program: rad u floti ili suradnja s vlastitim vozilom.",
  });

  const [path, setPath] = useState("A");
  const [hasOwnVehicle, setHasOwnVehicle] = useState(null);   // null | true | false
  const [form, setForm] = useState(EMPTY_FORM);
  const [step, setStep] = useState(1); // 1 = form, 2 = uploads, 3 = done
  const [submitting, setSubmitting] = useState(false);
  const [appId, setAppId] = useState(null);
  const [uploadedSlots, setUploadedSlots] = useState({});

  const requiredDocSlots = DOC_SLOTS.filter((d) => {
    if (!d.required.includes(path)) return false;
    if (d.vehicleDoc && hasOwnVehicle === false) return false;
    return true;
  });
  const uploadedCount = Object.keys(uploadedSlots).length;
  const complete = requiredDocSlots.every((d) => uploadedSlots[d.key]);

  const upd = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submitStep1 = async (e) => {
    e.preventDefault();
    if (hasOwnVehicle === null) {
      toast.error("Molimo odaberite imate li vlastito vozilo za rad.");
      return;
    }
    if (!form.consent_data_processing) {
      toast.error("Molimo prihvatite obradu podataka za nastavak.");
      return;
    }
    if (form.oib.length !== 11 || !/^\d+$/.test(form.oib)) {
      toast.error("OIB mora imati točno 11 znamenki.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        path,
        has_own_vehicle: hasOwnVehicle,
        ...form,
        driving_experience_years: form.driving_experience_years
          ? Number(form.driving_experience_years) : null,
        vehicle_year: hasOwnVehicle && form.vehicle_year
          ? Number(form.vehicle_year) : null,
      };
      // Strip Path B specific fields when path A + vice versa
      if (path === "A") {
        payload.company_name = null; payload.company_oib = null; payload.company_iban = null;
      } else {
        payload.employment_type = null; payload.driving_experience_years = null;
      }
      // Strip vehicle fields when no own vehicle
      if (!hasOwnVehicle) {
        payload.vehicle_make = ""; payload.vehicle_model = "";
        payload.vehicle_color = ""; payload.vehicle_vin = "";
        payload.vehicle_plates = ""; payload.vehicle_year = null;
        payload.eco_category = "";
      }
      const { data } = await axios.post(`${API}/driver-applications`, payload);
      setAppId(data.id);
      setStep(2);
      toast.success("Prijava zabilježena — molimo priložite dokumente.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Greška pri slanju prijave.");
    } finally {
      setSubmitting(false);
    }
  };

  const uploadFile = async (slot, file) => {
    if (!file) return;
    try {
      // 1) Compress camera photos client-side (max 2000px, target <2 MB).
      setUploadedSlots((u) => ({ ...u, [slot]: { compressing: true, name: file.name } }));
      const prepared = await compressImageFile(file);
      if (prepared.size > 30 * 1024 * 1024) {
        setUploadedSlots((u) => { const nu = { ...u }; delete nu[slot]; return nu; });
        toast.error("Datoteka prevelika (max 30 MB).");
        return;
      }
      // 2) Upload with a visible spinner + generous timeout.
      setUploadedSlots((u) => ({ ...u, [slot]: { uploading: true, name: prepared.name } }));
      const fd = new FormData();
      fd.append("slot", slot);
      fd.append("file", prepared);
      const { data } = await axios.post(
        `${API}/driver-applications/${appId}/files`,
        fd,
        { timeout: 120_000 },
      );
      setUploadedSlots((u) => ({
        ...u,
        [slot]: { name: prepared.name, size: data.size, uploading: false },
      }));
      toast.success(`Uploadano: ${prepared.name}`);
    } catch (err) {
      setUploadedSlots((u) => {
        const nu = { ...u }; delete nu[slot]; return nu;
      });
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = typeof detail === "string" ? detail : "";
      if (!msg) {
        if (status === 413) msg = "Datoteka je prevelika za slanje — pokušajte ponovno ili odaberite manju fotografiju.";
        else if (err?.code === "ECONNABORTED") msg = "Prekoračeno vrijeme učitavanja — provjerite internet vezu i pokušajte ponovno.";
        else msg = "Greška pri učitavanju. Pokušajte ponovno.";
      }
      toast.error(msg);
    }
  };

  const finish = () => setStep(3);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Toaster theme="dark" />
      <Header />
      <div className="pt-28 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <StepIndicator step={step} />
          {step === 1 && (
            <FormStep
              path={path}
              setPath={setPath}
              hasOwnVehicle={hasOwnVehicle}
              setHasOwnVehicle={setHasOwnVehicle}
              form={form}
              upd={upd}
              submitting={submitting}
              onSubmit={submitStep1}
            />
          )}
          {step === 2 && (
            <UploadStep
              path={path}
              requiredDocSlots={requiredDocSlots}
              uploadedSlots={uploadedSlots}
              uploadedCount={uploadedCount}
              complete={complete}
              onUpload={uploadFile}
              onFinish={finish}
            />
          )}
          {step === 3 && <DoneStep />}
        </div>
      </div>
    </div>
  );
}

// ---- Step 1: form -------------------------------------------------
function FormStep({ path, setPath, hasOwnVehicle, setHasOwnVehicle, form, upd, submitting, onSubmit }) {
  return (
    <>
      <div className="text-center mb-10">
        <p className="text-[10px] uppercase tracking-[0.32em] text-[#C5A059]">
          Juke Transfers · Ekipa vozača
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-light mt-3">
          Postani dio Juke ekipe
        </h1>
        <p className="text-zinc-400 text-sm mt-3 max-w-lg mx-auto leading-relaxed">
          Skaliramo profesionalnu premium transfer flotu diljem Dalmacije.
          Pridruži nam se kao vozač u floti ili poslovni partner s vlastitim vozilom.
        </p>
      </div>

      {/* Initial gate — vehicle ownership */}
      <div className="mb-6" data-testid="vehicle-ownership-gate">
        <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-3">
          1 · Imate li vlastito vozilo za rad?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <PathCard
            testId="vehicle-own-yes"
            active={hasOwnVehicle === true}
            onClick={() => setHasOwnVehicle(true)}
            icon={<Car className="w-6 h-6" />}
            title="Da, imam vlastito vozilo"
            desc="Trebat ću dostaviti sve dokumente vezano uz vozilo."
          />
          <PathCard
            testId="vehicle-own-no"
            active={hasOwnVehicle === false}
            onClick={() => setHasOwnVehicle(false)}
            icon={<Building2 className="w-6 h-6" />}
            title="Ne, trebam vozilo firme"
            desc="Juke dodjeljuje vozilo — preskačem dio o vozilu."
          />
        </div>
      </div>

      {hasOwnVehicle === null ? (
        <div className="rounded-2xl border border-white/5 bg-black/20 p-6 text-center text-zinc-500 text-sm">
          Odaberite jednu od opcija iznad za nastavak.
        </div>
      ) : (
      <>
      {/* Path selector */}
      <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-3">
        2 · Kako želite raditi?
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-testid="path-selector">
        <PathCard
          testId="path-A"
          active={path === "A"}
          onClick={() => setPath("A")}
          icon={<Briefcase className="w-6 h-6" />}
          title="Vozač u floti"
          desc="Tražim zaposlenje ili sezonski rad s vozilom firme."
        />
        <PathCard
          testId="path-B"
          active={path === "B"}
          onClick={() => setPath("B")}
          icon={<Building2 className="w-6 h-6" />}
          title="B2B Partner"
          desc="Imam obrt/firmu — želim ući u mrežu."
        />
      </div>

      <form onSubmit={onSubmit} className="space-y-6" data-testid="onboarding-form">
        <Section title="Osobni podaci" icon={<User className="w-4 h-4" />}>
          <Row2>
            <Field label="Ime" required><input required data-testid="fld-first_name" className={inp} value={form.first_name} onChange={upd("first_name")} /></Field>
            <Field label="Prezime" required><input required data-testid="fld-last_name" className={inp} value={form.last_name} onChange={upd("last_name")} /></Field>
          </Row2>
          <Row2>
            <Field label="Email" required><input required type="email" data-testid="fld-email" className={inp} value={form.email} onChange={upd("email")} /></Field>
            <Field label="Telefon" required><input required data-testid="fld-phone" placeholder="+385…" className={inp} value={form.phone} onChange={upd("phone")} /></Field>
          </Row2>
          <Row2>
            <Field label="OIB" required hint="11 znamenki"><input required maxLength={11} data-testid="fld-oib" className={inp} value={form.oib} onChange={upd("oib")} /></Field>
            <Field label="Datum rođenja">
              <input
                type="date"
                data-testid="fld-dob"
                className={`${inp} appearance-none [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:invert`}
                style={{ minHeight: "44px", colorScheme: "dark" }}
                value={form.date_of_birth}
                onChange={upd("date_of_birth")}
                onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch { /* gesture-only API */ } }}
                min="1940-01-01"
                max={new Date().toISOString().slice(0, 10)}
              />
            </Field>
          </Row2>
          <Row2>
            <Field label="Adresa"><input className={inp} value={form.address} onChange={upd("address")} /></Field>
            <Field label="Grad"><input className={inp} value={form.city} onChange={upd("city")} /></Field>
          </Row2>
        </Section>

        {path === "A" && (
          <Section title="Zapošljavanje" icon={<Briefcase className="w-4 h-4" />} testId="section-employment">
            <Row2>
              <Field label="Tip zaposlenja">
                <select className={inp} data-testid="fld-employment_type" value={form.employment_type} onChange={upd("employment_type")}>
                  <option value="full_time">Puno radno vrijeme</option>
                  <option value="part_time">Skraćeno radno vrijeme</option>
                  <option value="seasonal">Sezonski</option>
                </select>
              </Field>
              <Field label="Iskustvo u godinama"><input type="number" min={0} max={60} data-testid="fld-experience" className={inp} value={form.driving_experience_years} onChange={upd("driving_experience_years")} /></Field>
            </Row2>
          </Section>
        )}

        {path === "B" && (
          <Section title="Podaci o firmi" icon={<Building2 className="w-4 h-4" />} testId="section-company">
            <Row2>
              <Field label="Naziv firme / obrta" required><input required data-testid="fld-company_name" className={inp} value={form.company_name} onChange={upd("company_name")} /></Field>
              <Field label="OIB firme" required hint="11 znamenki"><input required maxLength={11} data-testid="fld-company_oib" className={inp} value={form.company_oib} onChange={upd("company_oib")} /></Field>
            </Row2>
            <Field label="IBAN firme (za obračun)"><input data-testid="fld-company_iban" className={inp} value={form.company_iban} onChange={upd("company_iban")} /></Field>
          </Section>
        )}

        <Section title="Bankovni podaci i licenca" icon={<FileCheck className="w-4 h-4" />}>
          <Row2>
            <Field label="Osobni IBAN"><input className={inp} value={form.iban} onChange={upd("iban")} /></Field>
            <Field label="Status taxi licence">
              <select className={inp} data-testid="fld-taxi_license_status" value={form.taxi_license_status} onChange={upd("taxi_license_status")}>
                <option value="not_yet">Još nemam</option>
                <option value="in_progress">U procesu</option>
                <option value="valid">Aktivna</option>
              </select>
            </Field>
          </Row2>
        </Section>

        {hasOwnVehicle && (
        <Section title="Podaci o vozilu" icon={<Car className="w-4 h-4" />} testId="section-vehicle">
          <Row2>
            <Field label="Marka" required><input required data-testid="fld-vehicle_make" className={inp} value={form.vehicle_make} onChange={upd("vehicle_make")} /></Field>
            <Field label="Model" required><input required data-testid="fld-vehicle_model" className={inp} value={form.vehicle_model} onChange={upd("vehicle_model")} /></Field>
          </Row2>
          <Row2>
            <Field label="Boja" required><input required className={inp} value={form.vehicle_color} onChange={upd("vehicle_color")} /></Field>
            <Field label="Godina" required><input required type="number" min={1990} max={2035} data-testid="fld-vehicle_year" className={inp} value={form.vehicle_year} onChange={upd("vehicle_year")} /></Field>
          </Row2>
          <Row2>
            <Field label="Registarske pločice" required><input required data-testid="fld-vehicle_plates" className={inp} value={form.vehicle_plates} onChange={upd("vehicle_plates")} /></Field>
            <Field label="VIN broj" required hint="17 znakova"><input required minLength={11} maxLength={20} data-testid="fld-vehicle_vin" className={inp} value={form.vehicle_vin} onChange={upd("vehicle_vin")} /></Field>
          </Row2>
          <Row2>
            <Field label="Ekološka kategorija (V.9 u prometnoj)" required>
              <select required data-testid="fld-eco_category" className={inp} value={form.eco_category} onChange={upd("eco_category")}>
                <option value="">— odaberite —</option>
                <option value="Euro 4">Euro 4</option>
                <option value="Euro 5">Euro 5</option>
                <option value="Euro 6">Euro 6</option>
                <option value="Euro 6d">Euro 6d</option>
              </select>
            </Field>
            <div />
          </Row2>
        </Section>
        )}

        <div className="pt-2 space-y-3">
          <label className="flex items-start gap-3 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" required data-testid="fld-consent" checked={form.consent_data_processing} onChange={upd("consent_data_processing")} className="mt-0.5 accent-[#C5A059]" />
            <span>
              Prihvaćam <a className="text-[#C5A059] underline" href="/hr/privacy" target="_blank" rel="noreferrer">obradu osobnih podataka</a> i suglasan sam s obradom u svrhu zapošljavanja / poslovne suradnje.
            </span>
          </label>
          <label className="flex items-start gap-3 text-xs text-zinc-500 cursor-pointer">
            <input type="checkbox" checked={form.consent_marketing} onChange={upd("consent_marketing")} className="mt-0.5 accent-[#C5A059]" />
            <span>Prihvaćam primati povremene obavijesti o Juke ekipi (opcionalno).</span>
          </label>
        </div>

        <button
          type="submit"
          data-testid="submit-onboarding-btn"
          disabled={submitting}
          className="w-full h-14 rounded-xl bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-60 text-black font-semibold uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-colors"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          Nastavi na upload dokumenata
        </button>
      </form>
      </>
      )}
    </>
  );
}

// ---- Step 2: uploads ---------------------------------------------
function UploadStep({ path: _path, requiredDocSlots, uploadedSlots, uploadedCount, complete, onUpload, onFinish }) {
  return (
    <>
      <div className="text-center mb-8">
        <p className="text-[10px] uppercase tracking-[0.32em] text-[#C5A059]">Korak 2 od 3</p>
        <h2 className="font-display text-3xl font-light mt-2">Priložite dokumente</h2>
        <p className="text-zinc-400 text-sm mt-2">
          Svaka datoteka do 25 MB · PDF, JPG, PNG, HEIC podržani
        </p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-4 py-1.5 text-xs">
          <span className="text-zinc-500">Uploadano</span>
          <span className="text-[#C5A059] font-semibold">{uploadedCount} / {requiredDocSlots.length}</span>
        </div>
      </div>
      <div className="space-y-3">
        {requiredDocSlots.map((doc) => (
          <UploadSlot
            key={doc.key}
            doc={doc}
            state={uploadedSlots[doc.key]}
            onFile={(f) => onUpload(doc.key, f)}
          />
        ))}
      </div>
      <button
        type="button"
        data-testid="onboarding-finish-btn"
        onClick={onFinish}
        disabled={!complete}
        className="w-full mt-8 h-14 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-semibold uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-colors"
      >
        <CheckCircle2 className="w-4 h-4" />
        Završi prijavu
      </button>
      <p className="text-[11px] text-zinc-600 text-center mt-3">
        Nastavak dostavljenim mailom · pregled u roku od 48 sati
      </p>
    </>
  );
}

function UploadSlot({ doc, state, onFile }) {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const busy = state?.uploading || state?.compressing;
  const done = state && !busy;

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (f) onFile(f);
  };

  return (
    <div
      data-testid={`slot-${doc.key}`}
      role="button"
      tabIndex={0}
      onClick={() => !busy && fileRef.current?.click()}
      onKeyDown={(e) => { if (e.key === "Enter" && !busy) fileRef.current?.click(); }}
      className={`block rounded-2xl border p-4 cursor-pointer transition-colors ${
        done ? "border-emerald-500/40 bg-emerald-500/5"
             : busy ? "border-[#C5A059]/40 bg-[#C5A059]/5"
             : "border-white/10 hover:border-[#C5A059]/40"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
          done ? "bg-emerald-500/15 text-emerald-300"
               : "bg-white/5 text-zinc-400"
        }`}>
          {busy ? <Loader2 className="w-5 h-5 animate-spin" />
             : done ? <CheckCircle2 className="w-5 h-5" />
             : <UploadCloud className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-medium">{doc.label}</p>
          <p className="text-[11px] text-zinc-500 truncate" data-testid={`slot-status-${doc.key}`}>
            {state?.compressing ? "Priprema fotografije…"
              : state?.uploading ? "Učitavanje…"
              : done ? state.name
              : "Odaberi datoteku ili fotografiraj"}
          </p>
        </div>
        {done && (
          <span className="text-[10px] uppercase tracking-widest text-emerald-400">Uploadano</span>
        )}
        {!done && !busy && (
          <button
            type="button"
            data-testid={`slot-camera-${doc.key}`}
            aria-label={`Fotografiraj — ${doc.label}`}
            onClick={(e) => { e.stopPropagation(); camRef.current?.click(); }}
            className="w-11 h-11 rounded-xl border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0 transition-colors"
          >
            <Camera className="w-5 h-5" />
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        disabled={busy}
        onChange={pick}
      />
      <input
        ref={camRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        disabled={busy}
        onChange={pick}
      />
    </div>
  );
}

// ---- Step 3: done -------------------------------------------------
function DoneStep() {
  return (
    <div className="text-center py-16" data-testid="onboarding-done">
      <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-5">
        <ShieldCheck className="w-8 h-8 text-emerald-300" />
      </div>
      <h2 className="font-display text-3xl font-light">Prijava zaprimljena</h2>
      <p className="text-zinc-400 text-sm mt-3 max-w-md mx-auto leading-relaxed">
        Administrator će pregledati vašu prijavu u roku od 48 sati. Kada bude odobrena,
        na email ćete dobiti privremenu lozinku i pristup vozačkom portalu.
      </p>
      <a
        href="/"
        className="inline-flex items-center gap-2 mt-8 h-11 px-6 rounded-xl border border-white/10 hover:border-[#C5A059]/40 hover:text-[#C5A059] text-zinc-300 text-xs uppercase tracking-widest transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Natrag na Juke Transfers
      </a>
    </div>
  );
}

// ---- primitives ---------------------------------------------------
const inp = "w-full h-11 rounded-xl bg-black/40 border border-white/10 px-3 text-sm text-white focus:outline-none focus:border-[#C5A059]/50 transition-colors";

function Row2({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
        {label} {required && <span className="text-[#C5A059]">*</span>}
        {hint && <span className="text-zinc-600 lowercase tracking-normal ml-1">· {hint}</span>}
      </div>
      {children}
    </label>
  );
}

function Section({ title, icon, testId, children }) {
  return (
    <div className="rounded-2xl bg-black/30 border border-white/5 p-5 space-y-3" data-testid={testId}>
      <div className="flex items-center gap-2 pb-1 border-b border-white/5 mb-2">
        <span className="text-[#C5A059]">{icon}</span>
        <h3 className="text-[10px] uppercase tracking-[0.24em] text-zinc-300">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function PathCard({ active, onClick, icon, title, desc, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-left rounded-2xl p-5 border transition-all ${
        active
          ? "border-[#C5A059] bg-[#C5A059]/10"
          : "border-white/10 bg-black/30 hover:border-[#C5A059]/40"
      }`}
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${
        active ? "bg-[#C5A059]/25 text-[#C5A059]" : "bg-white/5 text-zinc-400"
      }`}>{icon}</div>
      <p className="text-white font-medium text-sm">{title}</p>
      <p className="text-zinc-500 text-xs mt-1 leading-relaxed">{desc}</p>
    </button>
  );
}

function StepIndicator({ step }) {
  const steps = ["Podaci", "Dokumenti", "Gotovo"];
  return (
    <ol className="flex items-center justify-center gap-3 mb-10 text-[10px] uppercase tracking-widest">
      {steps.map((label, i) => (
        <li key={i} className={`flex items-center gap-3 ${i < step ? "text-[#C5A059]" : "text-zinc-600"}`}>
          <span className={`w-6 h-6 rounded-full border flex items-center justify-center text-[11px] ${
            i < step ? "border-[#C5A059] bg-[#C5A059] text-black font-semibold" : "border-white/10"
          }`}>{i + 1}</span>
          {label}
          {i < 2 && <span className="text-zinc-700">·</span>}
        </li>
      ))}
    </ol>
  );
}
